import clsx from "clsx";
import { startCase } from "lodash";
import { useReducedMotion } from "motion/react";
import { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { ErrorsDisplayContainer, FilterSearch } from "@ryde/controls";
import { useCrudEditContext } from "@ryde/redux";
import {
  canResolveAlways,
  EditFormMode,
  errorsCrudContentAtomFamily,
  IEditFormDrawerProps,
  IExtendableDto,
  IInjectedDrawerProps,
  isInnerItemEditingAtom,
  loadersEditFormDataSF,
  removeSpaces,
  useHasPermissions,
} from "@ryde/shared";

import { FormControlsWrapper, useEditFormInner } from "..";
import {
  BaseDrawer,
  CrudEditFormFooter,
  DrawerEntityDataLoader,
  DrawerHeader,
  FormFooter,
} from "../../drawers";
import { TabPanelStyled } from "../edit-form.styled";
import {
  getSystemParamCategoryHeaderColors,
  SYSTEM_PARAMS_TAB_PANEL_REDUCED_MOTION_VARIANTS,
  SYSTEM_PARAMS_TAB_PANEL_TRANSITION,
  SYSTEM_PARAMS_TAB_PANEL_VARIANTS,
  SystemParamsCategorySwitch,
} from "./parts";
import {
  SystemParamsDrawerContentStyled,
  SystemParamsHeaderTabBadgeStyled,
  SystemParamsHeaderTitleStyled,
  SystemParamsTabPanelContentStyled,
} from "./system-params.drawer-edit.styled";
import { getDrawerWidthByEntity } from "./system-params.drawer-edit.utils";
import { useSystemParamsEdit } from "./use-system-params-edit";

export const SystemParamsDrawerEditFormInner: FC<
  IEditFormDrawerProps & IInjectedDrawerProps
> = ({
  onCloseDrawer,
  resolveFn,
  drawerNumber,
  subTitle,
  mode = EditFormMode.CrudRoute,
  canResolve = canResolveAlways,
  customWidth,
}) => {
  const {
    data: { isEdit, isDuplicate, dto },
    entity,
  } = useCrudEditContext<IExtendableDto>();
  const isAddNew = !isEdit;

  const { t } = useTranslation();
  const {
    onDuplicateClick,
    onArchiveClick,
    closeSidebar: onCloseHandler,
    onSaveClick,
  } = useEditFormInner({ resolveFn, canResolve, onCloseDrawer });

  const {
    formState: { isDirty, isValid, errors },
  } = useFormContext();

  const loadingEditFormData = useRecoilValue(loadersEditFormDataSF(entity));
  const serverErrors = useRecoilValue(errorsCrudContentAtomFamily(entity));
  const isInnerItemEditing = useRecoilValue(isInnerItemEditingAtom);
  const shouldReduceMotion = useReducedMotion();

  const {
    searchValue,
    setSearchValue,
    isSearching,
    totalFound,
    tabIndex,
    uiTabIndex,
    handleTabChange,
    tabsNoInvisible,
    visibleTabViewModels,
    currentTabControls,
  } = useSystemParamsEdit();

  const { hasPermissions: hasUpdatePermissions } = useHasPermissions({
    entity,
    action: "update",
  });

  const isSaveDisabled =
    isInnerItemEditing ||
    !isValid ||
    (!isDirty && !isDuplicate) ||
    (Boolean(isEdit) && !hasUpdatePermissions);

  const unitTitle = t(`${entity}.single`);
  const addTitle = t(`general.ADD_NEW`, { entity: unitTitle });

  // Generate title: "Entity: Name" for edit, "Add new Entity" for adding
  const title = useMemo(() => {
    let titleText: string;
    if (isEdit) {
      if (dto) {
        const entityName = dto.title || dto.name || "";
        titleText = entityName ? `${unitTitle}: ${entityName}` : unitTitle;
      } else {
        titleText = unitTitle;
      }
    } else {
      titleText = addTitle;
    }

    if (subTitle) {
      titleText = `${titleText} ${subTitle}`;
    }

    return startCase(titleText);
  }, [addTitle, isEdit, dto, unitTitle, subTitle]);

  const currentTabLabel = useMemo(() => {
    const activeVm =
      visibleTabViewModels.find(vm => vm.originalIndex === uiTabIndex) ??
      visibleTabViewModels[0];
    const label = activeVm?.tab?.label;
    if (!label) {
      return undefined;
    }

    const translated = t(
      `selectListLabel.${removeSpaces(label.toLowerCase())}`,
    );

    return {
      rawLabel: label,
      displayLabel: translated || label,
    };
  }, [t, uiTabIndex, visibleTabViewModels]);

  const headerTitle = useMemo(() => {
    const headerColors = getSystemParamCategoryHeaderColors(
      currentTabLabel?.rawLabel,
    );

    return (
      <SystemParamsHeaderTitleStyled>
        <span>{title}</span>
        {Boolean(currentTabLabel?.displayLabel) && (
          <SystemParamsHeaderTabBadgeStyled $colors={headerColors}>
            {currentTabLabel?.displayLabel}
          </SystemParamsHeaderTabBadgeStyled>
        )}
      </SystemParamsHeaderTitleStyled>
    );
  }, [currentTabLabel, title]);

  const drawerWidth =
    customWidth !== undefined ? customWidth : getDrawerWidthByEntity(entity);

  const onCategorySelect = useEvent((originalIndex: number): void => {
    handleTabChange({} as React.SyntheticEvent, originalIndex);
  });

  return (
    <BaseDrawer
      onCloseDrawer={onCloseHandler}
      drawerNumber={drawerNumber || 0}
      customWidth={drawerWidth}
    >
      <DrawerHeader onCloseDrawer={onCloseHandler} title={headerTitle} />

      <SystemParamsDrawerContentStyled
        className={clsx(
          "drawer-form-with-image",
          isInnerItemEditing && "inner-item-editing-blur",
        )}
      >
        <div className="system-params__search">
          <FilterSearch
            searchValue={searchValue}
            onChange={setSearchValue}
            placeholder={`${t("general.SEARCH")}...`}
            fullWidth
          />
          {isSearching && (
            <div className="system-params__search-results">
              {t("general.NO_RESULTS_FOUND", { results: totalFound })}
            </div>
          )}
        </div>

        {!!(
          tabsNoInvisible.length &&
          (tabsNoInvisible.length > 1 || isSearching)
        ) && (
          <SystemParamsCategorySwitch
            tabViewModels={visibleTabViewModels}
            tabIndex={uiTabIndex}
            onTabSelect={onCategorySelect}
            isSearching={isSearching}
            isInnerItemEditing={isInnerItemEditing}
          />
        )}

        <TabPanelStyled>
          <ErrorsDisplayContainer
            serverErrors={serverErrors}
            formErrors={isValid ? undefined : errors}
          />

          <SystemParamsTabPanelContentStyled
            key={isSearching ? `search-${tabIndex}` : tabIndex}
            variants={
              shouldReduceMotion
                ? SYSTEM_PARAMS_TAB_PANEL_REDUCED_MOTION_VARIANTS
                : SYSTEM_PARAMS_TAB_PANEL_VARIANTS
            }
            initial="initial"
            animate="animate"
            transition={SYSTEM_PARAMS_TAB_PANEL_TRANSITION}
          >
            {!loadingEditFormData &&
              (isAddNew || Boolean(dto)) &&
              (!!currentTabControls.length || !isSearching) && (
                <FormControlsWrapper controls={currentTabControls} />
              )}

            {!loadingEditFormData &&
              isSearching &&
              !visibleTabViewModels.length && (
                <div className="system-params__no-results">
                  {t("general.NO_RESULTS")}
                </div>
              )}

            {loadingEditFormData && (
              <DrawerEntityDataLoader entityTitle={unitTitle} />
            )}
          </SystemParamsTabPanelContentStyled>
        </TabPanelStyled>
      </SystemParamsDrawerContentStyled>

      {mode !== EditFormMode.CrudRoute && (
        <FormFooter
          entity={entity}
          onSaveClick={onSaveClick}
          onCancelClick={onCloseHandler}
          isLoading={loadingEditFormData}
          isSaveDisabled={isSaveDisabled}
          isInnerItemEditing={isInnerItemEditing}
        />
      )}

      {mode === EditFormMode.CrudRoute && (
        <CrudEditFormFooter
          entity={entity}
          onSaveClick={onSaveClick}
          onCancelClick={onCloseHandler}
          isLoading={loadingEditFormData}
          isSaveDisabled={isSaveDisabled}
          isInnerItemEditing={isInnerItemEditing}
          duplicateClick={isEdit ? onDuplicateClick : undefined}
          isDuplicateDisabled={isAddNew}
          archiveClick={isEdit ? onArchiveClick : undefined}
          isArchiveDisabled={isAddNew}
          deleteMessage={t(`general.ARCHIVE_MESSAGE`, { entity: unitTitle })}
          duplicationMessage={t(`general.DUPLICATE_MESSAGE`, {
            entity: unitTitle,
          })}
        />
      )}
    </BaseDrawer>
  );
};
